<template>
    <student-backend-focus-layout
        content-bg-color="gray-125"
        header-bg-color="gray-125"
        exit-button-text="Exit Quiz"
        v-on:exit-page-clicked="exitQuiz"
        :page-title="quizTitle"
    >
        <template #page_header>
            <div class="-mb-4">
                <div
                    class="mx-auto text-center font-semibold text-gray-925 md:hidden"
                >
                    {{ quizTitle }}
                </div>
                <div class="text-center text-gray-700 md:hidden">
                    {{ questionIndex + 1 }} of {{ quizLength }}
                </div>
            </div>
        </template>
        <template #content>
            <div
                v-if="showQuiz"
                class="mx-auto mb-48 flex w-full max-w-3xl select-none flex-col items-center justify-center gap-4 md:mb-4 md:h-full"
            >
                <div
                    v-if="!submitting"
                    class="w-full border border-gray-300 bg-white p-8 shadow"
                >
                    <div v-if="question">
                        <div
                            v-if="question.is_tei || question.missed"
                            class="mb-4 flex items-center justify-start gap-2"
                        >
                            <div
                                v-if="question.is_tei"
                                class="rounded border border-green-500 px-2 py-1 text-[16px] text-green-500"
                            >
                                Technology Enhanced Item
                            </div>

                            <div
                                v-if="question.missed"
                                class="rounded border border-red-400 px-2 py-1 text-[16px] text-red-400"
                            >
                                <div>Past Mistake</div>
                            </div>
                        </div>

                        <div class="mb-8 text-gray-800">
                            <span v-html="question.content"></span>
                        </div>

                        <div class="flex flex-col gap-3">
                            <student-quiz-question-answer
                                v-for="answer in question.shuffled_answers"
                                :key="answer.id"
                                :answer="answer"
                                :selected="getAnswerSelected(answer.id)"
                                v-on:set-answer="setAnswer"
                            >
                            </student-quiz-question-answer>
                        </div>
                    </div>
                    <div
                        class="mb-4 mt-8 hidden text-center text-gray-700 md:block"
                    >
                        {{ questionIndex + 1 }} of {{ quizLength }}
                    </div>
                </div>
                <div
                    v-if="!submitting"
                    class="fixed bottom-0 mt-8 flex w-full justify-center gap-4 bg-white p-4 shadow-top md:relative md:mt-4 md:bg-transparent md:shadow-none"
                >
                    <div
                        v-if="canShowPrevQuestionBtn()"
                        class="w-1/2 rounded border border-gray-525 px-4 py-3 text-center text-gray-525 hover:cursor-pointer hover:bg-gray-300 md:w-auto"
                        @click="prevQuestion()"
                    >
                        Previous
                    </div>
                    <div
                        v-else
                        class="w-1/2 rounded border border-gray-525/50 px-4 py-3 text-center text-gray-525/50 hover:cursor-not-allowed hover:bg-gray-300 md:w-auto"
                    >
                        Previous
                    </div>
                    <div class="" v-if="questionIndex === quizLength - 1">
                        <div
                            v-if="canShowSubmitBtn()"
                            @click="submitQuiz()"
                            class="w-1/2 rounded bg-primary-525 px-4 py-3 text-center text-white hover:cursor-pointer hover:bg-primary-600 md:w-auto"
                        >
                            Submit
                        </div>
                        <div
                            v-else
                            class="w-1/2 rounded bg-primary-525 px-4 py-3 text-center text-white opacity-25 hover:cursor-not-allowed hover:bg-primary-600 md:w-auto"
                        >
                            Submit
                        </div>
                    </div>
                    <div class="" v-else>
                        <div
                            v-if="canShowNextQuestionBtn()"
                            class="w-1/2 rounded bg-primary-525 px-4 py-3 text-center text-white hover:cursor-pointer hover:bg-primary-600 md:w-auto"
                            @click="nextQuestion()"
                        >
                            Next
                        </div>
                        <div
                            v-else
                            class="w-1/2 rounded bg-primary-525 px-4 py-3 text-center text-white opacity-25 hover:cursor-not-allowed hover:bg-primary-600 md:w-auto"
                        >
                            Next
                        </div>
                    </div>
                </div>
            </div>

            <confirm-dialog
                ref="quizExitWarningConfirmDialog"
                size="2xl"
                confirm-button-label="Exit Quiz"
                confirm-button-css="rounded border bg-red-400 px-3 py-2 text-white hover:cursor-pointer hover:bg-red-300"
                cancel-button-label="Cancel"
                cancel-button-css="rounded border border-gray-700 px-3 py-2 text-gray-700 hover:cursor-pointer hover:bg-gray-300"
                v-on:cancel="exitCanceled"
                v-on:confirm="exitConfirmed"
            >
                <template #content>
                    <div class="flex flex-col">
                        <div class="text-[16px] font-semibold text-gray-925">
                            Are you sure you want to exit?
                        </div>
                        <div class="text-[16px] text-gray-700">
                            If you exit this quiz, your progress will
                            <span v-if="quizType !== 'assessment'">not</span> be
                            saved.
                        </div>
                    </div>
                </template>
            </confirm-dialog>
        </template>
    </student-backend-focus-layout>
</template>

<script>
import uniqid from "uniqid";
import StudentBackendFocusLayout from "@/_Layouts/NewStudentBackendFocusLayout.vue";
import StudentQuizQuestionAnswer from "@/Components/StudentQuizQuestionAnswer.vue";
import NewModal from "@/Components/NewModal.vue";
import ConfirmDialog from "@/Components/ConfirmDialog.vue";

export default {
    name: "StudentQuizShow",
    components: {
        ConfirmDialog,
        NewModal,
        StudentQuizQuestionAnswer,
        StudentBackendFocusLayout,
    },
    props: {
        quizId: {
            type: Number,
            default: null,
        },
        quizType: {
            type: String,
            default: "focus_area",
        },
        quizLength: {
            type: Number,
            default: 10,
        },
        quizTitle: {
            type: String,
            default: "",
        },
        focusArea: {
            type: Object,
            default: null,
        },
        question: {
            type: Object,
            default: {},
        },
        initStudentAnswer: {
            type: Array,
            default: {},
        },
        questionIndex: {
            type: Number,
            default: 0,
        },
        exitQuizUrl: {
            type: String,
            default: "/student/dashboard",
        },
    },
    data() {
        return {
            quizUUID: uniqid.process("quiz-"),
            questionTimer: null,
            optionOrder: [],
            timeSpent: 0,
            studentAnswer: this.initStudentAnswer,
            showQuiz: false,
            submitting: false,
        };
    },
    methods: {
        startQuiz: function () {
            let vm = this;
            let answerKeys = [];

            vm.question.shuffled_answers.forEach(function (answer) {
                answerKeys.push(answer.id);
            });

            vm.optionOrder = answerKeys;

            vm.questionTimer = setInterval(function () {
                vm.timeSpent++;
            }, 1000);

            vm.showQuiz = true;
        },
        closeQuiz: function () {
            clearInterval(this.questionTimer);

            // this.questionIndex = 0;
            // this.questionTimer = null;
            // this.questions = null;
            // this.studentAnswers = [];
            // this.showQuiz = false;
        },
        prevQuestion: function () {
            if (this.questionIndex > 0) {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });

                this.$inertia.get(
                    route("student.quiz.show", { student_quiz: this.quizId }),
                    {
                        q: this.questionIndex - 1,
                    }
                );
            }
        },
        nextQuestion: function () {
            if (this.questionIndex < this.quizLength) {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });

                this.submitQuestion();
            } else {
                this.submitQuiz();
            }
        },
        submitQuestion() {
            let vm = this;

            let selectedAnswers = [];

            vm.question.answers.forEach(function (studentAnswer) {
                selectedAnswers.push(vm.question["answers"][studentAnswer]);
            });

            window.dataLayer.push({
                event: "question-complete",
                "quiz-id": vm.quizUUID,
                "question-id": vm.question.id,
                "question-text": vm.question.stripped_content,
                "selected-answers": vm.selectedAnswers,
            });

            this.$inertia.get(route("student.quiz.submit-question"), {
                quiz_id: vm.quizId,
                question_index: vm.questionIndex,
                question_id: vm.question.id,
                student_answer: vm.studentAnswer,
                time_spent: vm.timeSpent,
                option_order: vm.optionOrder,
            });
        },
        setAnswer: function (answerId) {
            if (this.question.is_tei) {
                if (_.includes(this.studentAnswer, answerId)) {
                    this.studentAnswer.splice(
                        this.studentAnswer.indexOf(answerId),
                        1
                    );
                } else {
                    this.studentAnswer.push(answerId);
                }
            } else {
                this.studentAnswer = [answerId];
            }
        },
        getAnswerSelected: function (answerId) {
            return this.studentAnswer.length
                ? _.includes(this.studentAnswer, answerId)
                : false;
        },
        submitQuiz: function () {
            if (this.previewMode) {
                return;
            }

            let vm = this;

            vm.submitting = true;

            window.dataLayer.push({
                event: "quiz-complete",
                "quiz-id": vm.quizUUID,
                "quiz-topic": vm.quizTitle,
            });

            // tktk show submitting processing screen, submit question and then redirect to the results page y'all.

            this.submitQuestion();
        },
        canShowPrevQuestionBtn: function () {
            return this.questionIndex > 0;
        },
        canShowNextQuestionBtn: function () {
            return (
                Object.keys(this.studentAnswer).length !== 0 &&
                this.questionIndex < this.quizLength - 1
            );
        },
        canShowSubmitBtn: function () {
            return (
                this.studentAnswer.length &&
                this.questionIndex >= this.quizLength - 1
            );
        },
        warnBeforeExitingQuiz: function () {
            this.$refs.quizExitWarningConfirmDialog.openDialog();
        },
        exitConfirmed() {
            this.$inertia.get(this.exitQuizUrl);
        },
        exitCanceled() {},
        exitQuiz() {
            this.warnBeforeExitingQuiz();
        },
    },

    mounted() {
        this.startQuiz();
    },
};
</script>

<style scoped></style>
